/**
 * Everything a reseller can sell, with what it costs them: our normal price (Martin, 2026-09-24: resellers
 * buy at the normal price, their markup is their margin). One list for the admin's price tables, the
 * website preview and the customer demo, so a price set once shows the same everywhere.
 *
 * Costs are copies of the server's price tables and must match them:
 *  - website visits: constants/pricing.ts PRICING_MATRIX (Professional and Expert, one month);
 *  - SEO credits (Search Console and Local SEO share them): backend/routers/dashboard_overview.py GSC_PACKAGES;
 *  - YouTube: backend/smm_service.py PRODUCT_CONFIGS tiers (views, subscribers, likes, watch hours).
 */
import { PRICING_MATRIX, VOLUME_STEPS } from '../../constants/pricing';
import { priceFor, type SetupState } from './setup/setupState';

export type CatalogGroup = 'visits' | 'seo' | 'yt';
export type ProductId = 'professional' | 'expert' | 'seo' | 'yt_views' | 'yt_subs' | 'yt_likes' | 'yt_hours';

export interface CatalogItem {
  /** Key in SetupState.prices. Professional keeps the bare visit count, so prices saved before stay valid. */
  key: string;
  product: ProductId;
  qty: number;
  /** Extra units included for free (SEO bonus clicks). */
  bonus: number;
  /** What the reseller pays us, in USD. */
  cost: number;
}

export const PRODUCTS: Record<ProductId, { group: CatalogGroup; label: string; unit: string; blurb: string }> = {
  professional: { group: 'visits', label: 'Professional visits', unit: 'visits', blurb: 'Country targeting, residential IPs' },
  expert: { group: 'visits', label: 'Expert visits', unit: 'visits', blurb: 'City and state targeting, premium IPs' },
  seo: { group: 'seo', label: 'SEO credits', unit: 'clicks', blurb: 'Search Console and Local SEO clicks, one balance' },
  yt_views: { group: 'yt', label: 'YouTube views', unit: 'views', blurb: 'Refill included' },
  yt_subs: { group: 'yt', label: 'YouTube subscribers', unit: 'subscribers', blurb: 'No refill' },
  yt_likes: { group: 'yt', label: 'YouTube likes', unit: 'likes', blurb: 'Refill included' },
  yt_hours: { group: 'yt', label: 'Watch hours', unit: 'watch hours', blurb: 'Videos of 40 minutes or longer' },
};

// backend/routers/dashboard_overview.py GSC_PACKAGES: (credits, USD, bonus).
const SEO_PACKAGES: Array<[number, number, number]> = [
  [100, 25, 50], [500, 100, 250], [1000, 150, 500], [5000, 700, 2500],
  [10000, 1100, 5000], [20000, 1800, 10000], [50000, 3500, 25000], [100000, 5000, 50000],
];

// backend/smm_service.py PRODUCT_CONFIGS: (quantity, USD).
const YT_TIERS: Record<'yt_views' | 'yt_subs' | 'yt_likes' | 'yt_hours', Array<[number, number]>> = {
  yt_views: [[1000, 4.99], [5000, 19.99], [10000, 34.99], [25000, 74.99], [50000, 129.99]],
  yt_subs: [[100, 9.99], [250, 19.99], [500, 34.99], [1000, 59.99], [2500, 129.99]],
  yt_likes: [[500, 4.99], [1000, 8.99], [5000, 34.99], [10000, 59.99]],
  yt_hours: [[1000, 49.99], [2000, 89.99], [4000, 169.99]],
};

/** Our package sizes for website visits. Read once, before the customer demo swaps in the reseller's prices. */
export const VISIT_SIZES: number[] = VOLUME_STEPS.filter((v) => PRICING_MATRIX.professional[v]);
const VISIT_COST: Record<'professional' | 'expert', Record<number, number>> = {
  professional: Object.fromEntries(VISIT_SIZES.map((v) => [v, PRICING_MATRIX.professional[v][1]])),
  expert: Object.fromEntries(VISIT_SIZES.map((v) => [v, PRICING_MATRIX.expert[v]?.[1] ?? 0])),
};

export const itemKey = (product: ProductId, qty: number) => (product === 'professional' ? String(qty) : `${product}:${qty}`);

export function catalog(product: ProductId): CatalogItem[] {
  if (product === 'professional' || product === 'expert') {
    return VISIT_SIZES.map((qty) => ({ key: itemKey(product, qty), product, qty, bonus: 0, cost: VISIT_COST[product][qty] }));
  }
  if (product === 'seo') return SEO_PACKAGES.map(([qty, cost, bonus]) => ({ key: itemKey('seo', qty), product, qty, bonus, cost }));
  return YT_TIERS[product].map(([qty, cost]) => ({ key: itemKey(product, qty), product, qty, bonus: 0, cost }));
}

export const productsIn = (group: CatalogGroup) => (Object.keys(PRODUCTS) as ProductId[]).filter((p) => PRODUCTS[p].group === group);

/** What the customer pays: the admin's own price if set, else our cost plus the default markup. */
export const priceOf = (s: SetupState, item: CatalogItem) => s.prices[item.key] ?? priceFor(item.cost, s.markup);

/** Whether a package is on sale. Visit sizes follow `offered`; other packages are on unless taken off. */
export function isOffered(s: SetupState, item: CatalogItem): boolean {
  if (item.product === 'professional') return s.offered.includes(String(item.qty));
  if (item.product === 'expert') return s.expert && s.offered.includes(String(item.qty));
  return !s.hiddenPkgs.includes(item.key);
}

/** Whether a product is sold at all, given the services the reseller switched on. */
export function productOn(s: SetupState, product: ProductId): boolean {
  if (product === 'professional') return s.services.web;
  if (product === 'expert') return s.services.web && s.expert;
  if (product === 'seo') return s.services.gsc || s.services.local;
  return s.services.yt;
}

export const qtyLabel = (n: number) => (n >= 1000000 ? `${+(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${+(n / 1000).toFixed(1)}k` : String(n));
